<template>
    <div>
        <o-table ref="tableList"
                 :title="$t('sandoz.educational_courses') + ' - ' + contractNo"
                 :url="url"
                 :enable-new="false"
                 :enable-edit="false"
                 :enable-delete="false"
                 @loaded="onLoaded"
        >
            <template slot="head">
                <o-table-header column="course_name">{{$t('sandoz.course_name')}}</o-table-header>
                <o-table-header column="amount">{{$t('sandoz.amount_customer')}}</o-table-header>
                <o-table-header column="comment">{{$t('sandoz.comment')}}</o-table-header>
                <o-table-header column="course_location">{{$t('sandoz.course_location')}}</o-table-header>
                <o-table-header column="course_date">{{$t('sandoz.course_date')}}</o-table-header>
                <o-table-header column="participant_names">{{$t('sandoz.participant_names')}}</o-table-header>
                <o-table-header column="status">{{$t('sandoz.status')}}</o-table-header>
                <o-table-header>{{$t('sandoz.approved_for_invoicing')}}</o-table-header>
                <o-table-header v-if="status === 'Submitted'">{{$t('sandoz.verification_requirement')}}</o-table-header>
                <o-table-header v-if="status === 'Submitted'">{{$t('sandoz.proof')}}</o-table-header>
                <o-table-header class="text-right">{{$t('sandoz.actions')}}</o-table-header>
            </template>

            <template slot="head-controls">
                <th colspan="2">
                    <div class="d-flex align-items-center justify-content-end">
                        <button class="btn btn-primary btn-sm" @click="newCourse">{{ $t('sandoz.add') }} {{ $t('sandoz.course') }}</button>
                    </div>
                </th>
            </template>

            <template slot="body" slot-scope="props">
                <td>{{props.item.course_name}}</td>
                <td>{{props.item.amount}}</td>
                <td>
                    <div v-if="editingCommentId === props.item.id" class="d-flex">
                        <input v-model="editCommentText" class="form-control form-control-sm mr-1" />
                        <button class="btn btn-success btn-sm mr-1" @click="saveComment(props.item)"><fa icon="check"/></button>
                        <button class="btn btn-secondary btn-sm" @click="cancelEditComment"><fa icon="times"/></button>
                    </div>
                    <div v-else>
                        {{props.item.comment}}
                        <button class="btn btn-link btn-sm p-0 ml-1" @click="startEditComment(props.item)"><fa icon="pencil-alt"/></button>
                    </div>
                </td>
                <td>{{props.item.course_location}}</td>
                <td>{{props.item.course_date}}</td>
                <td>{{props.item.participant_names}}</td>
                <td>{{props.item.status ? $t('customer.' + props.item.status) : $t('sandoz.' + status.toLowerCase()) }}</td>
                <td>
                    <div class="custom-control custom-switch" @click.stop>
                        <input type="checkbox" class="custom-control-input" :id="'appCheck'+props.item.id" :checked="props.item.approved" @change="toggleApproval(props.item)">
                        <label class="custom-control-label" :for="'appCheck'+props.item.id"></label>
                    </div>
                </td>
                <td v-if="status === 'Submitted'">
                    <div class="custom-control custom-switch" v-if="props.item.type === 'ContractLine'" @click.stop>
                        <input type="checkbox" class="custom-control-input" :id="'verCheck'+props.item.id" :checked="props.item.verification_required" @change="toggleVerification(props.item)">
                        <label class="custom-control-label" :for="'verCheck'+props.item.id"></label>
                    </div>
                </td>
                <td v-if="status === 'Submitted'">
                    <a href="#" @click.prevent="showProofModal(props.item)" v-if="props.item.has_pictures">
                        <fa icon="eye"/>
                    </a>
                </td>
                <td class="text-right">
                                    <template v-if="!props.item.confirmDelete">
                                        <button type="button" class="btn btn-outline-danger btn-sm" @click="$set(props.item, 'confirmDelete', true)">
                                            <fa icon="trash-alt"/>
                                        </button>
                                    </template>
                                    <template v-else>
                                        <button type="button" class="btn btn-danger btn-sm mr-1" @click="deleteCourse(props.item)">
                                            {{ $t('core.confirm') || 'OK' }}
                                        </button>
                                        <button type="button" class="btn btn-outline-secondary btn-sm" @click="$set(props.item, 'confirmDelete', false)">
                                            <fa icon="times"/>
                                        </button>
                                    </template>
                </td>
            </template>
        </o-table>

        <div class="mb-2 mt-2">
            <div class="d-flex align-items-center justify-content-between">
                <div class="d-flex align-items-center" v-if="approvedSum !== null">
                    <span class="mr-3"><strong>{{$t('sandoz.approved_sum')}}:</strong> {{ approvedSum | formatNumber }}</span>
                    <span><strong>{{$t('sandoz.not_approved_sum')}}:</strong> {{ notApprovedSum | formatNumber }}</span>
                </div>
                <div class="d-flex align-items-center flex-wrap">
                    <div class="input-group input-group-sm mr-2 mb-1">
                        <div class="input-group-prepend">
                            <span class="input-group-text">{{$t('sandoz.actual_amount_of_educational')}}</span>
                        </div>
                        <input type="number" class="form-control" v-model.number="actualAmount" />
                        <div class="input-group-append">
                            <button class="btn btn-primary btn-sm" @click="saveActualAmount">
                                {{$t('core.save')}}
                            </button>
                        </div>
                    </div>
                    <div class="input-group input-group-sm mr-2 mb-1">
                        <div class="input-group-prepend">
                            <span class="input-group-text">{{$t('sandoz.verification_percentage')}}</span>
                        </div>
                        <input type="number" class="form-control" v-model.number="verificationPercentage" min="0" max="100" />
                        <div class="input-group-append">
                            <span class="input-group-text">%</span>
                        </div>
                        <div class="input-group-append">
                            <button class="btn btn-primary btn-sm" @click="saveVerificationPercentage">
                                {{$t('core.save')}}
                            </button>
                        </div>
                    </div>
                    <div class="custom-control custom-switch mr-2 mb-1" @click.stop>
                        <input type="checkbox" class="custom-control-input" id="invoicingBlockedCheck" :checked="invoicingBlocked" @change="toggleInvoicingBlocked">
                        <label class="custom-control-label" for="invoicingBlockedCheck">{{$t('sandoz.invoicing_blocked')}}</label>
                    </div>
                    <div class="custom-control custom-switch mr-2 mb-1" @click.stop>
                        <input type="checkbox" class="custom-control-input" id="contractSignedCheck" :checked="contractStatus === 'Signed'" @change="toggleContractSigned">
                        <label class="custom-control-label" for="contractSignedCheck">{{$t('sandoz.status_signed') || 'Signed'}}</label>
                    </div>
                    <button class="btn btn-success btn-sm mb-1 mr-1" @click="openApproveModal">{{ $t('sandoz.approve_all_courses') || 'Approve all Courses' }}</button>
                    <button class="btn btn-warning btn-sm mb-1" @click="openResetModal" v-if="contractStatus === 'Invoice Release Pending' || contractStatus === 'Signed'">{{ $t('sandoz.reset') }} {{ $t('sandoz.status') }}</button>
                </div>
            </div>
        </div>

        <o-action-toolbar>
            <div class="d-flex w-100">
                <o-button variant="outline-primary" @click.prevent="$router.push({name: 'sandoz.tracking.educational_courses'})">
                    {{$t('core.back')}}
                </o-button>
            </div>
        </o-action-toolbar>

        <div class="modal-backdrop fade show" v-if="showModalState || showResetModalState || showProofModalState || showApproveModalState"></div>
 
        <!-- Add Course Modal -->
        <div class="modal" tabindex="-1" role="dialog" :class="{'show': showModalState, 'fade': !showModalState}" style="display: block;" v-if="showModalState">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{$t('sandoz.add_course')}}</h5>
                        <button type="button" class="close" @click="closeModal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div class="form-group">
                            <label>{{$t('sandoz.course_name')}}</label>
                            <input v-model="newCourseData.course_name" class="form-control" />
                        </div>
                        <div class="form-group">
                            <label>{{$t('sandoz.amount_customer')}}</label>
                            <input v-model="newCourseData.amount" class="form-control" type="number"/>
                        </div>
                        <div class="form-group">
                            <label>{{$t('sandoz.comment')}}</label>
                            <textarea v-model="newCourseData.comment" class="form-control"></textarea>
                        </div>
                        <div class="form-group">
                            <label>{{$t('sandoz.course_location')}}</label>
                            <input v-model="newCourseData.course_location" class="form-control" />
                        </div>
                        <div class="form-group">
                            <label>{{$t('sandoz.course_date')}}</label>
                            <input v-model="newCourseData.course_date" class="form-control" />
                        </div>
                        <div class="form-group">
                            <label>{{$t('sandoz.participant_names')}}</label>
                            <textarea v-model="newCourseData.participant_names" class="form-control"></textarea>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeModal">{{$t('customer.close')}}</button>
                        <button type="button" class="btn btn-primary" @click="saveCourse">{{$t('customer.save')}}</button>
                    </div>
                </div>
            </div>
        </div>
        <!-- Reset Status Modal -->
        <div class="modal fade show" tabindex="-1" role="dialog" style="display: block;" v-if="showResetModalState">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{$t('sandoz.reset')}} {{$t('sandoz.status')}}</h5>
                        <button type="button" class="close" @click="closeResetModal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <p>{{$t('sandoz.reset_confirmation') || 'Are you sure you want to reset the status?'}}</p>
                        <div class="custom-control custom-checkbox mt-3">
                            <input type="checkbox" class="custom-control-input" id="resetDeleteProofsCheck" v-model="resetDeleteProofs">
                            <label class="custom-control-label" for="resetDeleteProofsCheck">{{$t('sandoz.delete_existing_verifications_proofs') || 'Delete existing verifications/proofs'}}</label>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeResetModal">{{$t('customer.close')}}</button>
                        <button type="button" class="btn btn-warning" @click="confirmResetStatus">{{$t('sandoz.reset')}}</button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Approve all Courses Modal -->
        <div class="modal fade show" tabindex="-1" role="dialog" style="display: block;" v-if="showApproveModalState">
            <div class="modal-dialog" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{ $t('sandoz.approve_all_courses') || 'Approve all Courses' }}</h5>
                        <button type="button" class="close" @click="closeApproveModal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <p>{{ $t('sandoz.approve_all_courses_explanation') || 'This approves all courses of this contract. The options below additionally update the switches on this page when ticked: enabling invoicing turns off "Invoicing blocked", and "Set to Signed" sets the contract status to Signed.' }}</p>
                        <div class="custom-control custom-checkbox mt-3">
                            <input type="checkbox" class="custom-control-input" id="approveEnableInvoicingCheck" v-model="approveEnableInvoicing">
                            <label class="custom-control-label" for="approveEnableInvoicingCheck">{{ $t('sandoz.enable_invoicing') || 'Enabled Invoicing' }}</label>
                        </div>
                        <div class="custom-control custom-checkbox mt-2">
                            <input type="checkbox" class="custom-control-input" id="approveSetSignedCheck" v-model="approveSetSigned">
                            <label class="custom-control-label" for="approveSetSignedCheck">{{ $t('sandoz.set_to_signed') || 'Set to Signed' }}</label>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" @click="closeApproveModal">{{$t('customer.close')}}</button>
                        <button type="button" class="btn btn-success" @click="confirmApproveAll">{{ $t('sandoz.approve_all_courses') || 'Approve all Courses' }}</button>
                    </div>
                </div>
            </div>
        </div>


        <!-- Proof Modal -->
        <div class="modal" tabindex="-1" role="dialog" :class="{'show': showProofModalState, 'fade': !showProofModalState}" style="display: block;" v-if="showProofModalState">
            <div class="modal-dialog modal-lg" role="document">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{$t('sandoz.proof')}}</h5>
                        <button type="button" class="close" @click="closeProofModal" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                    </div>
                    <div class="modal-body">
                        <div v-if="proofLoading" class="text-center p-3">
                            <fa icon="spinner" spin /> Loading...
                        </div>
                        <template v-else-if="proofVerification">
                            <div class="row mb-3">
                                <div class="col-md-6">
                                    <p class="mb-1"><strong>{{$t('sandoz.status')}}:</strong>
                                        <span v-if="proofVerification.status" :class="proofBadge(proofVerification.status)" class="badge ml-1">
                                            {{ $t('sandoz.' + proofVerification.status.toLowerCase()) }}
                                        </span>
                                    </p>
                                    <p class="mb-1" v-if="proofVerification.planned_verification"><strong>{{$t('sandoz.planned_verification')}}:</strong> {{ proofVerification.planned_verification | formatDatetime }}</p>
                                    <p class="mb-1" v-if="proofVerification.actual_verification"><strong>{{$t('sandoz.actual_verification')}}:</strong> {{ proofVerification.actual_verification | formatDatetime }}</p>
                                </div>
                                <div class="col-md-6">
                                    <p class="mb-1" v-if="proofVerification.user"><strong>{{$t('core.user')}}:</strong> {{ proofVerification.user.first_name }} {{ proofVerification.user.name }}</p>
                                    <p class="mb-1" v-if="proofVerification.note"><strong>{{$t('core.note')}}:</strong> {{ proofVerification.note }}</p>
                                </div>
                            </div>
                            <hr>
                            <div v-if="proofPictures.length" class="d-flex flex-wrap">
                                <div v-for="(pic, idx) in proofPictures" :key="idx" class="mr-2 mb-2" style="max-width: 242px;">
                                        <div class="card" style="width: 242px; min-height: 320px;">
                                            <template v-if="!pic.confirmDelete">
                                                <div :style="{'background-image': 'url('+pic.src+')', 'height': '180px', 'background-size': 'cover', 'background-position': 'center'}" class="card-img-top cursor-pointer" @click="downloadProofFile(pic)"></div>
                                                <div class="card-body p-2 d-flex flex-column">
                                                    <p class="card-text mb-1 small text-truncate font-weight-bold" :title="pic.alt">{{ pic.alt }}</p>
                                                    <div class="mb-2">
                                                        <p class="card-text mb-0 small" v-if="pic.latitude"><strong>{{$t('sandoz.latitude')}}:</strong> {{ pic.latitude }}</p>
                                                        <p class="card-text mb-0 small" v-if="pic.longitude"><strong>{{$t('sandoz.longitude')}}:</strong> {{ pic.longitude }}</p>
                                                        <p class="card-text mb-0 small" v-if="pic.distance !== null && pic.distance !== undefined"><strong>{{$t('sandoz.distance')}}:</strong> {{ pic.distance | formatDistance }}</p>
                                                        <p class="card-text mb-0 small text-muted" v-if="pic.comment" :title="pic.comment">
                                                            <strong>{{$t('sandoz.comment')}}:</strong> {{ pic.comment }}
                                                        </p>
                                                    </div>
                                                    <div class="d-flex mt-auto pt-2">
                                                        <a :href="pic.url" target="_blank" class="btn btn-xs btn-outline-primary flex-grow-1 mr-1">
                                                            <fa icon="download"/> {{$t('sandoz.download')}}
                                                        </a>
                                                        <button type="button" class="btn btn-xs btn-outline-danger" @click="$set(pic, 'confirmDelete', true)">
                                                            <fa icon="trash-alt"/>
                                                        </button>
                                                    </div>
                                                </div>
                                            </template>
                                            <template v-else>
                                                <div class="card-body p-3 d-flex flex-column justify-content-center bg-light text-center" style="min-height: 320px;">
                                                    <div class="mb-3">
                                                       <fa icon="trash-alt" class="text-danger mb-2" style="font-size: 2rem;"/>
                                                       <p class="mb-0 font-weight-bold">{{ $t('sandoz.delete_confirmation') || 'Are you sure?' }}</p>
                                                    </div>
                                                    <div class="d-flex flex-column px-2">
                                                        <button type="button" class="btn btn-danger mb-2 btn-sm" @click="deleteProofFile(pic)">
                                                            {{ $t('core.confirm') || 'OK' }}
                                                        </button>
                                                        <button type="button" class="btn btn-outline-secondary btn-sm" @click="$set(pic, 'confirmDelete', false)">
                                                            {{ $t('core.cancel') || 'Cancel' }}
                                                        </button>
                                                    </div>
                                                </div>
                                            </template>
                                        </div>
                                </div>
                            </div>
                            <div v-else class="text-muted text-center p-3">
                                {{ $t('sandoz.no_pictures') || 'No files available' }}
                            </div>
                        </template>
                    </div>
                    <div class="modal-footer d-flex justify-content-between">
                        <div class="d-flex align-items-center">
                            <div class="mr-2" style="max-width: 200px;">
                                <input type="text" v-model="proofFileUploadComment" class="form-control" :placeholder="$t('sandoz.comment') || 'Comment'">
                            </div>
                            <div class="custom-file mr-2" style="max-width: 250px;">
                                <input type="file" class="custom-file-input" id="proofFileUpload" @change="onProofFileSelected" :disabled="uploading" multiple>
                                <label class="custom-file-label" for="proofFileUpload">{{ uploadFileNames || $t('core.choose_file') }}</label>
                            </div>
                            <button type="button" class="btn btn-primary" @click="uploadProofFile" :disabled="!selectedProofFiles || uploading">
                                <fa v-if="uploading" icon="spinner" spin />
                                <fa v-else icon="upload" />
                                {{$t('sandoz.upload') || 'Upload'}}
                            </button>
                        </div>
                        <button type="button" class="btn btn-secondary" @click="closeProofModal">{{$t('customer.close')}}</button>
                    </div>
                </div>
            </div>
        </div>
        <div class="modal-backdrop fade show" v-if="showProofModalState"></div>
    </div>
</template>

<script>
import axios from 'axios'

export default {
    data() {
        return {
            status: 'Draft',
            contractStatus: '',
            contractId: null,
            contractNo: '',
            showModalState: false,
            showResetModalState: false,
            showApproveModalState: false,
            approveEnableInvoicing: true,
            approveSetSigned: true,
            resetDeleteProofs: true,
            resetActionType: null, // "standard" or "contract"
            newCourseData: {
                course_name: '',
                amount: '',
                comment: '',
                course_location: '',
                course_date: '',
                participant_names: ''
            },
            url: `/api/sandoz/educational/courses/${this.$route.params.id}`,
            approvedSum: null,
            notApprovedSum: null,
            verificationPercentage: 20,
            actualAmount: null,
            invoicingBlocked: false,
            editingCommentId: null,
            editCommentText: '',
            showProofModalState: false,
            proofVerification: null,
            proofPictures: [],
            proofLoading: false,
            proofCourseItem: null,
            selectedProofFiles: null,
            uploadFileNames: '',
            proofFileUploadComment: '',
            uploading: false
        }
    },
    created() {
        this.loadContractInfo()
    },
    methods: {
        loadContractInfo() {
            const contractId = this.$route.params.id
            axios.get(`/api/sandoz/educational/courses/${contractId}/contract-info`)
                .then(response => {
                    this.status = response.data.status
                    this.contractStatus = response.data.contract_status
                    this.contractId = response.data.contract_id
                    this.contractNo = response.data.contract_no
                    this.verificationPercentage = response.data.verification_percentage ?? 20
                    this.actualAmount = response.data.actual_amount_of_educational
                    this.invoicingBlocked = response.data.invoicing_blocked
                })
        },
        onLoaded(data) {
            if (data && data.approved_sum !== undefined) {
                this.approvedSum = data.approved_sum
                this.notApprovedSum = data.not_approved_sum
            }
        },
        openResetModal(actionType = 'contract') {
            this.resetActionType = actionType
            this.resetDeleteProofs = true
            this.showResetModalState = true
        },
        closeResetModal() {
            this.showResetModalState = false
            this.resetActionType = null
        },
        confirmResetStatus() {
            const contractId = this.$route.params.id
            const payload = { delete_proofs: this.resetDeleteProofs }
            
            if (this.resetActionType === 'contract') {
                axios.post(`/api/sandoz/educational/courses/${contractId}/reset-contract-status`, payload)
                    .then(() => {
                        this.$notify({ type: 'success', text: 'Contract status reset' })
                        this.loadContractInfo()
                        this.$refs.tableList.search()
                        this.closeResetModal()
                    })
                    .catch(error => {
                        console.error('Failed to reset contract status:', error)
                        this.$notify({ type: 'error', text: 'Failed to reset contract status' })
                    })
            } else {
                axios.post(`/api/sandoz/educational/courses/${contractId}/reset`, payload)
                    .then(() => {
                        this.$notify({ type: 'success', text: 'Status reset to Draft' })
                        this.loadContractInfo()
                        this.$refs.tableList.search()
                        this.closeResetModal()
                    })
                    .catch(error => {
                        console.error('Failed to reset status:', error)
                        this.$notify({ type: 'error', text: 'Failed to reset status' })
                    })
            }
        },
        resetStatus() {
            this.openResetModal('standard');
        },
        newCourse() {
            this.newCourseData = { course_name: '', amount: '', comment: '', course_location: '', course_date: '', participant_names: '' }
            this.showModalState = true
        },
        closeModal() {
            this.showModalState = false
        },
        saveCourse() {
            const contractId = this.$route.params.id
            axios.post(`/api/sandoz/educational/courses/${contractId}/store`, this.newCourseData)
                .then(() => {
                    this.$notify({ type: 'success', text: 'Course added' })
                    this.closeModal()
                    this.$refs.tableList.search()
                })
                .catch(error => {
                    console.error('Failed to add course:', error)
                    this.$notify({ type: 'error', text: 'Failed to add course' })
                })
        },
        deleteCourse(course) {
            const contractId = this.$route.params.id
            axios.delete(`/api/sandoz/educational/courses/${contractId}/delete/${course.id}`, { params: { type: course.type } })
                .then(() => {
                    this.$notify({ type: 'success', text: 'Course deleted' })
                    this.$refs.tableList.search()
                })
                .catch(error => {
                    console.error('Failed to delete course:', error)
                    this.$notify({ type: 'error', text: 'Failed to delete course' })
                })
        },
        toggleVerification(course) {
            const contractId = this.$route.params.id
            axios.post(`/api/sandoz/educational/courses/${contractId}/toggle-verification/${course.id}`)
                .then(response => {
                    this.$notify({ type: 'success', text: 'Verification requirement updated' })
                    course.verification_required = response.data.new_state
                })
                .catch(error => {
                    console.error('Failed to toggle verification:', error)
                    this.$notify({ type: 'error', text: 'Failed to toggle verification' })
                    course.verification_required = !course.verification_required
                })
        },
        toggleApproval(course) {
            const contractId = this.$route.params.id
            axios.post(`/api/sandoz/educational/courses/${contractId}/toggle-approval/${course.id}`)
                .then(response => {
                    this.$notify({ type: 'success', text: 'Approval updated' })
                    course.approved = response.data.new_state
                    this.$refs.tableList.search()
                })
                .catch(error => {
                    console.error('Failed to toggle approval:', error)
                    this.$notify({ type: 'error', text: 'Failed to toggle approval' })
                    course.approved = !course.approved
                })
        },
        toggleInvoicingBlocked() {
            const contractId = this.$route.params.id
            axios.post(`/api/sandoz/educational/courses/${contractId}/toggle-invoicing-blocked`)
                .then(response => {
                    this.invoicingBlocked = response.data.new_state
                    this.$notify({ type: 'success', text: 'Invoicing blocked status updated' })
                })
                .catch(error => {
                    console.error('Failed to toggle invoicing blocked:', error)
                    this.$notify({ type: 'error', text: 'Failed to toggle invoicing blocked' })
                })
        },
        toggleContractSigned() {
            const contractId = this.$route.params.id
            const makeSigned = this.contractStatus !== 'Signed'
            axios.post(`/api/sandoz/educational/courses/${contractId}/toggle-contract-signed`, { signed: makeSigned })
                .then(response => {
                    this.contractStatus = response.data.new_state
                    this.$notify({ type: 'success', text: 'Contract status updated' })
                })
                .catch(error => {
                    console.error('Failed to update contract status:', error)
                    this.$notify({ type: 'error', text: 'Failed to update contract status' })
                })
        },
        startEditComment(course) {
            this.editingCommentId = course.id
            this.editCommentText = course.comment || ''
        },
        cancelEditComment() {
            this.editingCommentId = null
            this.editCommentText = ''
        },
        saveComment(course) {
            const contractId = this.$route.params.id
            axios.post(`/api/sandoz/educational/courses/${contractId}/update-comment/${course.id}`, { comment: this.editCommentText })
                .then(() => {
                    this.$notify({ type: 'success', text: 'Comment updated' })
                    course.comment = this.editCommentText
                    this.cancelEditComment()
                })
                .catch(error => {
                    console.error('Failed to update comment:', error)
                    this.$notify({ type: 'error', text: 'Failed to update comment' })
                })
        },
        saveVerificationPercentage() {
            const contractId = this.$route.params.id
            axios.post(`/api/sandoz/educational/courses/${contractId}/update-verification-percentage`, { verification_percentage: this.verificationPercentage })
                .then(() => {
                    this.$notify({ type: 'success', text: 'Verification percentage updated' })
                })
                .catch(error => {
                    console.error('Failed to update verification percentage:', error)
                    this.$notify({ type: 'error', text: 'Failed to update verification percentage' })
                })
        },
        saveActualAmount() {
            axios.post('/api/sandoz/educational/contracts/set-actual-education', {
                item: {
                    id: this.contractId,
                    actual_amount_of_educational: this.actualAmount
                }
            })
                .then(() => {
                    this.$notify({ type: 'success', text: 'Actual educational amount updated' })
                })
                .catch(error => {
                    console.error('Failed to update actual educational amount:', error)
                    this.$notify({ type: 'error', text: 'Failed to update actual educational amount' })
                })
        },
        openApproveModal() {
            this.approveEnableInvoicing = true
            this.approveSetSigned = true
            this.showApproveModalState = true
        },
        closeApproveModal() {
            this.showApproveModalState = false
        },
        confirmApproveAll() {
            const contractId = this.$route.params.id
            // 1. Approve all courses. 2. Optionally apply the switch changes for each ticked box.
            const requests = [axios.post(`/api/sandoz/educational/courses/${contractId}/approve-all`)]

            // "Enabled Invoicing" -> ensure "Invoicing blocked" is OFF. The endpoint is a pure
            // toggle, so only call it when invoicing is currently blocked.
            if (this.approveEnableInvoicing && this.invoicingBlocked) {
                requests.push(axios.post(`/api/sandoz/educational/courses/${contractId}/toggle-invoicing-blocked`))
            }

            // "Set to Signed" -> ensure the contract status is Signed (idempotent on the backend).
            if (this.approveSetSigned && this.contractStatus !== 'Signed') {
                requests.push(axios.post(`/api/sandoz/educational/courses/${contractId}/toggle-contract-signed`, { signed: true }))
            }

            Promise.all(requests)
                .then(() => {
                    this.$notify({ type: 'success', text: 'All courses approved' })
                    this.closeApproveModal()
                    this.loadContractInfo()
                    this.$refs.tableList.search()
                })
                .catch(error => {
                    console.error('Failed to approve all courses:', error)
                    this.$notify({ type: 'error', text: 'Failed to approve all courses' })
                })
        },
        resetContractStatus() {
            this.openResetModal('contract');
        },
        showProofModal(course) {
            if (!course.verification_id) return
            this.proofCourseItem = course
            this.showProofModalState = true
            this.proofLoading = true
            this.proofVerification = null
            this.proofPictures = []

            axios.get(`/api/sandoz/verifications/${course.verification_id}?include=user,pictures,contract_line`)
                .then(response => {
                    this.proofVerification = response.data
                    if (this.proofVerification.pictures && this.proofVerification.pictures.length) {
                        this.proofVerification.pictures.forEach(item => {
                            var src = '/verifications/' + course.verification_id + '/' + item.picture
                            var extension = item.picture.split('.').pop().toLowerCase()
                            if (extension !== 'png' && extension !== 'jpg' && extension !== 'jpeg') {
                                if (extension === 'doc' || extension === 'docx') {
                                    extension = 'doc'
                                }
                                if (extension === 'xlsx' || extension === 'xls') {
                                    extension = 'xls'
                                }
                                if (extension === 'html' || extension === 'eml' || extension === 'txt' || extension === 'msg') {
                                    extension = 'html'
                                }
                                src = '/images/' + extension + '-icon.png'
                            }
                            this.proofPictures.push({
                                src: src,
                                alt: item.picture,
                                latitude: item.latitude,
                                longitude: item.longitude,
                                distance: item.distance,
                                comment: item.comment,
                                url: '/api/verifications/download/' + course.verification_id + '/' + item.picture
                            })
                        })
                    }
                })
                .catch(error => {
                    console.error('Failed to load verification:', error)
                    this.$notify({ type: 'error', text: 'Failed to load verification details' })
                })
                .finally(() => {
                    this.proofLoading = false
                })
        },
        closeProofModal() {
            this.showProofModalState = false
            this.proofVerification = null
            this.proofPictures = []
            this.proofCourseItem = null
            this.selectedProofFiles = null
            this.uploadFileNames = ''
            this.uploading = false
        },
        downloadProofFile(item) {
            window.open(item.url, '_blank')
        },
        deleteProofFile(item) {
            axios.post('/api/verifications/delete-picture', {
                id: this.proofCourseItem.verification_id,
                image: item.alt
            })
                .then(() => {
                    this.$notify({ type: 'success', text: 'File deleted' })
                    this.showProofModal(this.proofCourseItem)
                })
                .catch(error => {
                    console.error('Failed to delete file:', error)
                    this.$notify({ type: 'error', text: 'Failed to delete file' })
                })
        },
        proofBadge(state) {
            if (!state) return ''
            var badges = {
                'pending': 'badge-primary',
                'verified': 'badge-success',
                'failed': 'badge-danger'
            }
            return badges[state] || 'badge-secondary'
        },
        onProofFileSelected(event) {
            this.selectedProofFiles = event.target.files
            if (this.selectedProofFiles && this.selectedProofFiles.length > 0) {
                this.uploadFileNames = Array.from(this.selectedProofFiles).map(f => f.name).join(', ')
            } else {
                this.uploadFileNames = ''
            }
        },
        async uploadProofFile() {
            if (!this.selectedProofFiles || !this.selectedProofFiles.length || !this.proofCourseItem) return
            this.uploading = true

            try {
                for (let i = 0; i < this.selectedProofFiles.length; i++) {
                    const formData = new FormData()
                    formData.append('image', this.selectedProofFiles[i])
                    formData.append('id', this.proofCourseItem.verification_id)
                    formData.append('contract', this.proofCourseItem.address_id)
                    formData.append('lat', '0')
                    formData.append('long', '0')
                    formData.append('comment', this.proofFileUploadComment)
 
                    await axios.post('/api/verifications/upload-picture?from=customer', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    })
                }

                this.$notify({ type: 'success', text: 'File(s) uploaded successfully' })
                this.selectedProofFiles = null
                this.uploadFileNames = ''
                this.proofFileUploadComment = ''
                // Reset file input
                const fileInput = document.getElementById('proofFileUpload')
                if (fileInput) fileInput.value = ''
                // Refresh proof pictures
                this.showProofModal(this.proofCourseItem)
            } catch (error) {
                console.error('Failed to upload file:', error)
                this.$notify({ type: 'error', text: 'Failed to upload file' })
            } finally {
                this.uploading = false
            }
        }
    }
}
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
</style>